import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton } from "@/hq/HQPrimitives";
import { asText } from "@/hq/asText";
import HQTeamUsers from "@/hq/settings/HQTeamUsers";
import HQPricesPanel from "@/hq/settings/HQPricesPanel";
import SettingsAgreementsPanel from "@/hq/settings/SettingsAgreementsPanel";
import HQTestModePanel from "@/hq/settings/HQTestModePanel";

const TABS = [
  { key: "team",     label: "Team & Users" },
  { key: "prices",   label: "Prices" },
  { key: "agreements", label: "Agreements & Terms" },
  { key: "emails",   label: "Emails & Logs" },
  { key: "payments", label: "Payments" },
  { key: "campaigns", label: "Campaigns" },
  { key: "test-mode", label: "Test Mode" },
  { key: "system",   label: "System" },
];

function Metric({ label, value, tone = "orange" }) {
  const toneMap = { orange: "text-[#FF6A00]", green: "text-[#0FA958]", red: "text-[#C1350B]", slate: "text-[#0A0A0A]" };
  return (
    <Card className="p-4">
      <div className={`text-[10px] tracking-[0.28em] uppercase font-bold ${toneMap[tone]}`}>{label}</div>
      <div className="mt-2 font-black text-2xl">{value ?? "—"}</div>
    </Card>
  );
}

function TeamPanel() {
  return <HQTeamUsers/>;
}

const DELIVERY_LABEL = {
  DELIVERED: "Delivered",
  SENT: "Sent",
  DELIVERY_DELAYED: "Delayed",
  BOUNCED: "Bounced",
  COMPLAINED: "Complained",
  FAILED: "Failed",
};

const DELIVERY_CLASS = {
  DELIVERED:        "bg-[#E7F7EE] text-[#0F7B3E] border-[#B7E4C7]",
  SENT:             "bg-[#F0F0EE] text-[#4A4A4A] border-[#DADADA]",
  DELIVERY_DELAYED: "bg-[#FFF4E1] text-[#8A5A00] border-[#F3D89A]",
  BOUNCED:          "bg-[#FDE8E1] text-[#8B2A0F] border-[#F3B9A2]",
  COMPLAINED:       "bg-[#F4E8FD] text-[#5B1D8B] border-[#D9B9F3]",
  FAILED:           "bg-[#FDE8E8] text-[#8B0F0F] border-[#F3A2A2]",
};

function DeliveryChip({ row }) {
  const state = row?.delivery_status || (row?.status === "sent" ? "SENT" : null);
  if (!state) return null;
  const label = DELIVERY_LABEL[state] || state;
  const cls   = DELIVERY_CLASS[state] || DELIVERY_CLASS.SENT;
  const reason = row?.sanitized_provider_error;
  return (
    <span
      data-testid={`hq-email-delivery-${state}`}
      title={reason || (state === "DELIVERED" ? "Confirmed delivered by remote MX" : label)}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black tracking-[0.16em] uppercase border ${cls}`}
    >
      {label}
    </span>
  );
}

function EmailsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { hqApi.get("/control/settings/emails").then((r) => setData(r.data)); }, []);
  const c = data?.counts;
  return (
    <div className="space-y-4" data-testid="hq-settings-emails">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Accepted" value={c?.sent ?? "—"} tone="green"/>
        <Metric label="Delivered" value={c?.delivered ?? "—"} tone="green"/>
        <Metric label="Delayed" value={c?.delivery_delayed ?? "—"} tone="orange"/>
        <Metric label="Bounced" value={c?.bounced_delivery ?? "—"} tone="red"/>
        <Metric label="Failed" value={c?.failed ?? "—"} tone="red"/>
        <Metric label="Complained" value={c?.complained ?? "—"} tone="red"/>
        <Metric label="Skipped" value={c?.skipped ?? "—"}/>
        <Metric label="Sending" value={c?.sending ?? "—"}/>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Opens" value={c?.opens ?? "—"} tone="green"/>
        <Metric label="Clicks" value={c?.clicks ?? "—"} tone="green"/>
        <Metric label="Campaign bounces" value={c?.bounces ?? "—"} tone="red"/>
        <Metric label="Unsubscribed" value={c?.unsubscribed ?? "—"} tone="slate"/>
      </div>
      <Card className="p-6">
        <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Recent activity</div>
        <ul className="mt-3 space-y-1 text-sm" data-testid="hq-emails-recent-list">
          {(data?.recent || []).slice(0, 20).map((row, i) => (
            <li key={i} className="border-b border-[#EAEAEA] py-2" data-testid="hq-emails-recent-row">
              <div className="flex justify-between items-start gap-3">
                <span className="font-semibold flex-1 min-w-0 truncate">{row.subject || row.comm_type}</span>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] font-black tracking-[0.16em] uppercase ${row.status === "sent" ? "text-[#0FA958]" : row.status === "failed" ? "text-[#C1350B]" : "text-[#8A6D00]"}`}>{row.status}</span>
                  <DeliveryChip row={row}/>
                </div>
              </div>
              <div className="text-[12px] text-[#8A8A8A]">
                to {row.to_email || "—"} · {row.created_at ? new Date(row.created_at).toLocaleString() : "—"}
              </div>
              {row.sanitized_provider_error && (
                <div
                  data-testid="hq-email-sanitized-error"
                  className="mt-1 text-[11px] text-[#8B2A0F] bg-[#FFF7F4] border border-[#F3D0C4] rounded px-2 py-1"
                >
                  Provider: {row.sanitized_provider_error}
                </div>
              )}
            </li>
          ))}
          {(data?.recent || []).length === 0 && <EmptyState title="No email history yet" testId="hq-emails-empty"/>}
        </ul>
      </Card>
    </div>
  );
}

function PaymentsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { hqApi.get("/control/settings/payments").then((r) => setData(r.data)); }, []);
  return (
    <div className="space-y-4" data-testid="hq-settings-payments">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-6">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Stripe</div>
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between border-b border-[#EAEAEA] py-2"><span>Status</span><strong className={data?.stripe?.configured ? "text-[#0FA958]" : "text-[#C1350B]"}>{data?.stripe?.configured ? "Configured" : "Not configured"}</strong></div>
            <div className="flex justify-between border-b border-[#EAEAEA] py-2"><span>Currencies</span><strong>{data?.stripe?.currencies?.join(", ")}</strong></div>
            <div className="flex justify-between py-2"><span>Mode</span><strong className="uppercase">{data?.stripe?.mode}</strong></div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">PayPal</div>
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between border-b border-[#EAEAEA] py-2"><span>Status</span><strong className={data?.paypal?.configured ? "text-[#0FA958]" : "text-[#C1350B]"}>{data?.paypal?.configured ? "Configured" : "Not configured"}</strong></div>
            <div className="flex justify-between border-b border-[#EAEAEA] py-2"><span>Currencies</span><strong>{data?.paypal?.currencies?.join(", ")}</strong></div>
            <div className="flex justify-between border-b border-[#EAEAEA] py-2"><span>Mode</span><strong className="uppercase">{data?.paypal?.mode}</strong></div>
            <div className="flex justify-between py-2"><span>Webhook ID set</span><strong>{data?.paypal?.webhook_id_set ? "Yes" : "No"}</strong></div>
          </div>
        </Card>
      </div>
      <Card className="p-6">
        <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Currency routing</div>
        <div className="mt-3 text-sm text-[#4A4A4A]">
          <div>AED → <strong className="text-[#0A0A0A]">Stripe</strong></div>
          <div>GBP, USD, EUR → <strong className="text-[#0A0A0A]">PayPal</strong></div>
        </div>
      </Card>
    </div>
  );
}

function CampaignSettingsPanel() {
  return (
    <Card className="p-6" data-testid="hq-settings-campaign">
      <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Campaign defaults</div>
      <div className="mt-3 space-y-2 text-sm">
        <div className="flex justify-between border-b border-[#EAEAEA] py-2"><span>Default send time</span><strong>10:00 Europe/London</strong></div>
        <div className="flex justify-between border-b border-[#EAEAEA] py-2"><span>Bulk cap per request</span><strong>200 recipients</strong></div>
        <div className="flex justify-between border-b border-[#EAEAEA] py-2"><span>Intro re-send suppression window</span><strong>30 days</strong></div>
        <div className="flex justify-between py-2"><span>Sender identity</span><strong>nathan@orangebysite.com</strong></div>
      </div>
      <div className="mt-4">
        <Link to="/hq/campaigns" className="text-[11px] tracking-[0.16em] uppercase font-black text-[#FF6A00] hover:underline">→ Manage campaign templates</Link>
      </div>
    </Card>
  );
}

function SystemPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { hqApi.get("/control/settings/system").then((r) => setData(r.data)); }, []);
  const c = data?.counts;
  return (
    <div className="space-y-4" data-testid="hq-settings-system">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Businesses" value={c?.businesses ?? "—"}/>
        <Metric label="Contacts" value={c?.contacts ?? "—"}/>
        <Metric label="Websites" value={c?.websites ?? "—"}/>
        <Metric label="Subscriptions" value={c?.subscriptions ?? "—"}/>
        <Metric label="Payments" value={c?.payments ?? "—"}/>
        <Metric label="Invoices" value={c?.invoices ?? "—"}/>
        <Metric label="Campaigns" value={c?.campaigns ?? "—"}/>
        <Metric label="Prospects" value={c?.prospects ?? "—"}/>
      </div>
      <Card className="p-6">
        <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Audit log · admin events</div>
        <ul className="mt-3 space-y-1 text-sm">
          {(data?.recent_admin_events || []).slice(0, 20).map((a, i) => (
            <li key={i} className="flex justify-between border-b border-[#EAEAEA] py-2">
              <span>{asText(a.description) || asText(a.event_type)}</span>
              <span className="text-[#8A8A8A] text-[11px]">{a.created_at ? new Date(a.created_at).toLocaleString() : "—"}</span>
            </li>
          ))}
          {(data?.recent_admin_events || []).length === 0 && <EmptyState title="No admin activity yet" testId="hq-audit-empty"/>}
        </ul>
        <div className="mt-4">
          <Link to="/hq/activity" className="text-[11px] tracking-[0.16em] uppercase font-black text-[#FF6A00] hover:underline">→ Full activity log</Link>
        </div>
      </Card>
    </div>
  );
}

export default function HQSettings() {
  const [tab, setTab] = useState("team");
  return (
    <div className="space-y-5" data-testid="hq-settings">
      <div>
        <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Settings</div>
        <h1 className="mt-1 font-black text-2xl md:text-3xl tracking-[-0.02em]">Team, emails, payments &amp; system</h1>
      </div>

      <div className="flex gap-2 overflow-x-auto" data-testid="hq-settings-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`hq-settings-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-[11px] font-black tracking-[0.16em] uppercase whitespace-nowrap ${
              tab === t.key ? "bg-[#0A0A0A] text-white" : "bg-white border border-[#EAEAEA] hover:bg-[#F6F5F2]"
            }`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "team"     && <TeamPanel/>}
      {tab === "prices"   && <HQPricesPanel/>}
      {tab === "agreements" && <SettingsAgreementsPanel/>}
      {tab === "emails"   && <EmailsPanel/>}
      {tab === "payments" && <PaymentsPanel/>}
      {tab === "campaigns" && <CampaignSettingsPanel/>}
      {tab === "test-mode" && <HQTestModePanel/>}
      {tab === "system"   && <SystemPanel/>}
    </div>
  );
}
